import { useEffect, useState } from "react";
import { useDocStore } from "../../state/docStore";
import {
  TEMPLATE_FIELD_META,
  type ProjectDocField,
  type TemplateQuestionField,
} from "../../lib/types";
import { Button } from "../common/Button";
import { exportDoc } from "../../lib/apiClient";
import "./DocPreview.css";

interface DocPreviewProps {
  onSubmit: () => void;
  canSubmit: boolean;
  /** Human-readable list of what's still required before submitting. */
  submitBlockers: string[];
}

/** Renders one field's value, or an italic placeholder when empty. Highlights briefly on change. */
function Section({
  num,
  field,
  label,
}: {
  num: number;
  field: TemplateQuestionField;
  label: string;
}) {
  const { doc } = useDocStore();
  const highlight = useHighlight(field);
  const value = doc[field];

  return (
    <div className="doc__section">
      <div className="doc__section-title">
        <span className="doc__section-num">{num}.</span>
        {label}
      </div>
      <div className={`doc__section-body ${highlight ? "doc__highlight" : ""}`}>
        {value ? value : <span className="doc__placeholder">— not yet filled —</span>}
      </div>
    </div>
  );
}

/** Returns true briefly after `field` changes, then auto-clears the store flag. */
function useHighlight(field: ProjectDocField): boolean {
  const { recentlyChanged, clearRecent } = useDocStore();
  const active = recentlyChanged.has(field);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!active) return;
    setShow(true);
    const clearFlag = setTimeout(() => clearRecent(field), 1400);
    const hide = setTimeout(() => setShow(false), 1400);
    return () => {
      clearTimeout(clearFlag);
      clearTimeout(hide);
    };
  }, [active, field, clearRecent]);

  return show;
}

export function DocPreview({ onSubmit, canSubmit, submitBlockers }: DocPreviewProps) {
  const { doc, streaming, resetDraft } = useDocStore();
  const [downloading, setDownloading] = useState<"docx" | "pdf" | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const handleReset = () => {
    if (
      window.confirm(
        "Start a new document? This clears the current document and conversation. This cannot be undone.",
      )
    ) {
      void resetDraft();
    }
  };

  const company = doc.companyName.trim() || "Company";
  const mainHighlight = useHighlight("mainGoal");
  const stretchHighlight = useHighlight("stretchGoals");

  const handleDownload = async (format: "docx" | "pdf") => {
    setDownloadError(null);
    setDownloading(format);
    try {
      await exportDoc(format, doc);
    } catch (err) {
      setDownloadError(
        err instanceof Error ? err.message : "Download failed. Try again.",
      );
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="preview">
      <div className="preview__toolbar">
        <span className="preview__toolbar-label">
          Live document {streaming && "· updating…"}
        </span>
        <div className="preview__actions">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleReset}
            disabled={streaming}
            title="Clear everything and start a new document"
          >
            New
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => handleDownload("docx")}
            loading={downloading === "docx"}
            disabled={downloading !== null}
          >
            Word
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => handleDownload("pdf")}
            loading={downloading === "pdf"}
            disabled={downloading !== null}
          >
            PDF
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={onSubmit}
            disabled={!canSubmit}
            title={
              canSubmit
                ? "Submit your scoped project"
                : "Add your name, a valid contact email, and a main goal before submitting"
            }
          >
            Submit
          </Button>
        </div>
      </div>

      {downloadError && (
        <div
          style={{
            color: "var(--aisg-red)",
            fontSize: "var(--text-sm)",
            padding: "var(--space-2) var(--space-5)",
            background: "var(--aisg-red-soft)",
          }}
        >
          {downloadError}
        </div>
      )}

      {!canSubmit && submitBlockers.length > 0 && (
        <div className="preview__submithint">
          To submit, add: {submitBlockers.join(", ")}.
        </div>
      )}

      <div className="preview__scroll">
        <article className="doc">
          <h1 className="doc__title">Project Planning Phase - {company}</h1>
          <div className="doc__subtitle">
            LLM Applications Developer Programme (LADP) · AI Singapore
          </div>
          {(doc.participantName.trim() || doc.contactEmail.trim()) && (
            <div className="doc__preparedby">
              <strong>Submitted by:</strong> {doc.participantName.trim() || "—"}
              {doc.contactEmail.trim() && ` · ${doc.contactEmail.trim()}`}
            </div>
          )}
          <div className="doc__rule" aria-hidden="true" />

          {/* Problem Statement comes first (template field 1) … */}
          <Section num={1} field="problemStatement" label="Problem Statement" />

          {/* … then the scoping block just under it (CLAUDE.md §5). */}
          <div className="doc__scope">
            <div className="doc__scope-label">Scope · 1-month focus</div>
            <div
              className={`doc__scope-main ${mainHighlight ? "doc__highlight" : ""}`}
            >
              {doc.mainGoal ? (
                <>
                  <strong>Main goal:</strong> {doc.mainGoal}
                </>
              ) : (
                <span className="doc__placeholder">
                  Main goal not yet defined — the assistant can help you set this.
                </span>
              )}
            </div>
            {doc.stretchGoals.some((g) => g.trim()) && (
              <div className={stretchHighlight ? "doc__highlight" : ""}>
                <div className="doc__scope-stretch-title">
                  Stretch goals (prioritised)
                </div>
                <ol>
                  {doc.stretchGoals
                    .filter((g) => g.trim())
                    .map((g, i) => (
                      <li key={i}>{g}</li>
                    ))}
                </ol>
              </div>
            )}
          </div>

          {/* Remaining template questions, fields 2..14 in order. */}
          {TEMPLATE_FIELD_META.slice(1).map((m, i) => (
            <Section key={m.key} num={i + 2} field={m.key} label={m.label} />
          ))}

          <div className="doc__disclaimer">
            <strong>Disclaimer.</strong> This document was prepared with the
            assistance of an AI tool provided by AI Singapore, intended as a guide
            only. AI Singapore makes no representations or warranties, express or
            implied, as to the accuracy, completeness, reliability or suitability of
            any information generated or suggested by the tool, and does not
            independently verify it. It remains the sole responsibility of the user
            to review, verify and confirm that all information is accurate, complete
            and appropriate before submission. AI Singapore accepts no liability for
            any errors or omissions, or for any loss or damage of any kind arising
            from reliance on this document or use of the tool.
          </div>
        </article>
      </div>
    </div>
  );
}
